import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { 
  HomeIcon, 
  DocumentTextIcon, 
  CogIcon, 
  AdjustmentsHorizontalIcon, 
  ScissorsIcon,
  BeakerIcon,
  Bars3Icon,
  XMarkIcon,
  HeartIcon,
  ShieldCheckIcon,
  UserIcon,
  ArrowRightEndOnRectangleIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Define navigation items with their required permissions - Healthcare Domain
const navigationItems = [
  { name: 'Dashboard', href: '/', icon: HomeIcon, permission: null },
  { name: 'Document Review', href: '/classify', icon: DocumentTextIcon, permission: 'classify_documents' },
  { name: 'HIPAA Compliance', href: '/hipaa', icon: ShieldCheckIcon, permission: 'manage_compliance' },
  { name: 'Clinical Protocols', href: '/clinical', icon: ClipboardDocumentListIcon, permission: 'manage_protocols' },
  { name: 'Safety Monitoring', href: '/safety', icon: ExclamationTriangleIcon, permission: 'safety_monitoring' },
  { name: 'Testing', href: '/testing', icon: BeakerIcon, permission: 'classify_documents' },
  { name: 'Settings', href: '/settings', icon: AdjustmentsHorizontalIcon, permission: 'manage_configs' },
  { name: 'Users', href: '/users', icon: UserIcon, permission: 'manage_users' },
];

export default function Navbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, hasPermission, logout } = useAuth();
  const userMenuRef = useRef(null);

  // Filter navigation items based on user permissions
  const visibleNavigation = navigationItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Left side - Logo */}
          <div className="flex items-center min-w-0 flex-shrink-0 mr-8">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/healthfabric-logo.png" 
                alt="HealthFabric Logo" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold text-gray-900">HealthFabric</span>
            </Link>
          </div>

          {/* Center - Main Navigation (Desktop only) */}
          <div className="hidden lg:flex lg:items-center lg:space-x-6 flex-1">
            {visibleNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    isActive
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900 border-transparent hover:border-gray-300'
                  } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200`}
                >
                  <item.icon className="w-4 h-4 mr-2" />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Right side - User Menu */}
          <div className="flex items-center space-x-4">
            {/* User dropdown - Desktop */}
            <div className="hidden md:block relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md px-2 py-1"
              >
                <UserIcon className="w-5 h-5" />
                <span className="font-medium">{user?.username}</span>
                <ChevronDownIcon className="w-4 h-4" />
              </button>

              {/* User dropdown menu */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <div className="px-4 py-2 text-sm text-gray-900 border-b border-gray-100">
                    <div className="font-medium">{user?.full_name}</div>
                    <div className="text-gray-500">{user?.email}</div>
                    <div className="text-xs text-blue-600 capitalize mt-1">
                      {user?.role?.replace('_', ' ')} Role
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <ArrowRightEndOnRectangleIcon className="w-4 h-4 mr-2" />
                      Sign Out
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-5 w-5" />
                ) : (
                  <Bars3Icon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden" id="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-gray-50 border-t border-gray-200">
              {/* User info - Mobile */}
              <div className="px-3 py-3 border-b border-gray-200 mb-2">
                <div className="text-sm text-gray-900 mb-2">
                  <div className="font-medium">{user?.full_name}</div>
                  <div className="text-gray-500">{user?.email}</div>
                  <div className="text-xs text-blue-600 capitalize mt-1">
                    {user?.role?.replace('_', ' ')} Role
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                >
                  <ArrowRightEndOnRectangleIcon className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>

              {/* Navigation items */}
              {visibleNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-red-50 border-red-500 text-red-700'
                        : 'border-transparent text-gray-600 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-900'
                    } block pl-3 pr-4 py-3 border-l-4 text-base font-medium transition-colors duration-200`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="flex items-center">
                      <item.icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}